import React, { useState } from 'react';
import styled from 'styled-components';

import { DashboardTableCard } from './DashboardTableCard';
import { GridActionsCellItem, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { InfoOutlined, VisibilityOutlined } from '@mui/icons-material';
import { useTheme } from '../../styles';
import { ViewError } from '@components/Modals/Logs/ViewError';
import { Tooltip } from '@common/Tooltip';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '@store/index';
import { useNavigate } from 'react-router-dom';
import { refresh } from '@store/async';
import { useSnackbar } from 'notistack';
import { Dayjs } from 'dayjs';
import { QueryFunctionContext } from '@tanstack/react-query';

import {
  listAdminBuckets,
  listAdminExecutionErrors,
  listAdminWorkflows,
  listAdminWorkflowTypes,
  listAdminWorkspaces,
} from '@requests/admin/reporting.requests';

export const DashboardContainer = styled.div`
  display: flex;
  flex-direction: column;

  background: ${({ theme }) => theme.background.standard};
  height: calc(100vh - 100px);
  width: calc(100vw - 6rem);
  padding: 0 2rem;
`;

export const FullScreenTable = styled.div`
  display: flex;
  width: 100%;
  height: 95%;
`;

const Grid = styled.div<{ columns: number }>`
  display: grid;
  grid-template-columns: ${({ columns }) => `repeat(${columns}, 1fr)`};
  gap: 1rem;
  width: 100%;
  height: 100%;
`;

const GridContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 1rem;
  padding-bottom: 2rem;
`;

export type DashboardCardType = {
  queryKey: string;
  queryFn: (params: QueryFunctionContext<[string, { startDate: Dayjs | null; endDate: Dayjs | null }]>) => Promise<any>;
  title: string;
  columns: any[];
};

interface Props {
  startDate: Dayjs | null;
  endDate: Dayjs | null;
}

export const Dashboard: React.FC<Props> = ({ startDate, endDate }) => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const [fullScreenCard, setFullScreenCard] = useState<string | null>(null);
  const [logError, setLogError] = useState<string | null>(null);

  const openAccountPage = async (path: string, user_id: string, org_id: string) => {
    localStorage.setItem('internalUserId', user_id);
    localStorage.setItem('internalOrgId', org_id);

    const result = await dispatch(refresh());

    if (result.type === 'auth/refresh/fulfilled' && !result?.payload?.error) {
      navigate(path, { replace: true });
    }

    if (result?.type === 'auth/refresh/rejected') {
      enqueueSnackbar('Failed to login to users account.', {
        variant: 'error',
        autoHideDuration: 10000,
      });
    }
  };

  const cards: DashboardCardType[] = [
    {
      queryKey: 'admin_workspaces',
      queryFn: listAdminWorkspaces,
      title: 'Workspaces',
      columns: [
        {
          field: 'service_name',
          headerName: 'Name',
          flex: 100,
          sortable: true,
        },
        {
          field: 'service_id',
          headerName: 'Domain',
          flex: 130,
          sortable: true,
          renderCell: (params: GridRenderCellParams<any>) => {
            const row = params.row;

            return <pre>{`/execute/${row.service_id}/`}</pre>;
          },
        },
        {
          field: 'created_by',
          headerName: 'Created By',
          flex: 100,
          sortable: true,
        },
        {
          field: 'created_at',
          headerName: 'Created At (UTC)',
          flex: 100,
          sortable: true,
          renderCell: (params: GridRenderCellParams<any>) => {
            const { row } = params;

            return <div>{new Date(row.updated_at).toLocaleString()}</div>;
          },
        },
        {
          field: 'actions',
          type: 'actions',
          headerName: 'Actions',
          width: 100,
          cellClassName: 'actions',
          getActions: (params: GridRowParams<any>) => {
            const { row } = params;
            return [
              <Tooltip title='View Workspace' key='view' arrow placement='top'>
                <GridActionsCellItem
                  icon={<VisibilityOutlined />}
                  label='View Workspace'
                  onClick={() => openAccountPage(`/${row.service_id}/workflow`, row.user_id, row.org_id)}
                  color='inherit'
                />
              </Tooltip>,
            ];
          },
        },
      ],
    },
    {
      queryKey: 'admin_workflows',
      queryFn: listAdminWorkflows,
      title: 'Workflows',
      columns: [
        {
          field: 'name',
          headerName: 'Name',
          flex: 120,
          sortable: true,
        },
        {
          field: 'service_path',
          headerName: 'Endpoint',
          flex: 280,
          sortable: true,
          renderCell: (params: GridRenderCellParams<any>) => {
            const row = params.row;

            return <pre>{`/execute/${row.service_id}/${row.service_path}`}</pre>;
          },
        },
        {
          field: 'created_by',
          headerName: 'Created By',
          flex: 70,
          sortable: true,
        },
        fullScreenCard && {
          field: 'created_at',
          headerName: 'Created At (UTC)',
          flex: 110,
          sortable: true,
          renderCell: (params: GridRenderCellParams<any>) => {
            const { row } = params;

            return <div>{new Date(row.updated_at).toLocaleString()}</div>;
          },
        },
        fullScreenCard && {
          field: 'updated_at',
          headerName: 'Updated At (UTC)',
          flex: 110,
          sortable: true,
          renderCell: (params: GridRenderCellParams<any>) => {
            const { row } = params;

            return <div>{new Date(row.updated_at).toLocaleString()}</div>;
          },
        },
        {
          field: 'actions',
          type: 'actions',
          headerName: 'Actions',
          width: 100,
          cellClassName: 'actions',
          getActions: (params: GridRowParams<any>) => {
            const { row } = params;
            return [
              <Tooltip title='View Workflow' key='view' arrow placement='top'>
                <GridActionsCellItem
                  icon={<VisibilityOutlined />}
                  label='View'
                  onClick={() => openAccountPage(`/${row.service_id}/workflow/${row.flow_id}`, row.user_id, row.org_id)}
                  color='inherit'
                />
              </Tooltip>,
            ];
          },
        },
      ].filter(Boolean),
    },
    {
      queryKey: 'admin_workflow_types',
      queryFn: listAdminWorkflowTypes,
      title: 'Workflow Types',
      columns: [
        {
          field: 'trigger_type',
          headerName: 'Trigger Type',
          flex: 150,
          sortable: true,
        },
        {
          field: 'trigger_count',
          headerName: 'Count',
          flex: 150,
          type: 'number',
          sortable: true,
        },
      ],
    },
    {
      queryKey: 'admin_execution_errors',
      queryFn: listAdminExecutionErrors,
      title: 'Execution Errors',
      columns: [
        {
          field: 'error_code',
          headerName: 'Error Title',
          flex: 80,
          sortable: true,
          renderCell: (params: GridRenderCellParams<any>) => {
            const { row } = params;

            try {
              const errorParsed = JSON.parse(row.error);
              return <div>{errorParsed?.title || 'No Title'}</div>;
            } catch {
              return <div>No Title</div>;
            }
          },
        },
        fullScreenCard && {
          field: 'error',
          headerName: 'Error Detail',
          flex: 250,
          sortable: true,
          renderCell: (params: GridRenderCellParams<any>) => {
            const { row } = params;

            try {
              const errorParsed = JSON.parse(row.error);
              return <div>{errorParsed?.detail || 'Unable to determine error message.'}</div>;
            } catch {
              return <div>Unable to determine error message.</div>;
            }
          },
        },
        {
          field: 'email',
          headerName: 'User',
          flex: 80,
          sortable: true,
        },
        {
          field: 'platform',
          headerName: 'Platform',
          flex: 50,
          sortable: true,
        },
        fullScreenCard && {
          field: 'executed_nodes',
          headerName: 'Executed Nodes',
          flex: 50,
          sortable: true,
          renderCell: (params: GridRenderCellParams<any>) => {
            const { row } = params;

            return (
              <div>
                {row.executed_nodes}/{row.total_nodes}
              </div>
            );
          },
        },
        fullScreenCard && {
          field: 'created_at',
          headerName: 'Created At (UTC)',
          flex: 100,
          sortable: true,
          renderCell: (params: GridRenderCellParams<any>) => {
            const { row } = params;

            return <div>{new Date(row.created_at).toLocaleString()}</div>;
          },
        },
        {
          field: 'actions',
          type: 'actions',
          headerName: 'Actions',
          width: 100,
          cellClassName: 'actions',
          getActions: (params: GridRowParams<any>) => {
            const { row } = params;
            return [
              <GridActionsCellItem
                key='delete'
                icon={<InfoOutlined sx={{ color: row?.error ? theme.telnyx.red : undefined }} />}
                label='Delete'
                disabled={!row?.error}
                onClick={() => {
                  setLogError(row?.error ?? 'There was no error information stored for this log.');
                }}
                color='inherit'
              />,
              <Tooltip title='View Workflow' key='view' arrow placement='top'>
                <GridActionsCellItem
                  icon={<VisibilityOutlined />}
                  label='View'
                  onClick={() => openAccountPage(`/${row.service_id}/workflow/${row.flow_id}`, row.user_id, row.org_id)}
                  color='inherit'
                />
              </Tooltip>,
            ];
          },
        },
      ].filter(Boolean),
    },
    {
      queryKey: 'admin_buckets',
      queryFn: listAdminBuckets,
      title: 'Buckets',
      columns: [
        {
          field: 'name',
          headerName: 'Name',
          flex: 150,
          sortable: true,
        },
        {
          field: 'region',
          headerName: 'Region',
          flex: 100,
          sortable: true,
        },
        {
          field: 'service_id',
          headerName: 'Workspace',
          flex: 100,
          sortable: true,
        },
        fullScreenCard && {
          field: 'username',
          headerName: 'Username',
          flex: 100,
          sortable: true,
        },
        fullScreenCard && {
          field: 'created_at',
          headerName: 'Created At (UTC)',
          flex: 100,
          sortable: true,
          renderCell: (params: GridRenderCellParams<any>) => {
            const { row } = params;

            return <div>{new Date(row.updated_at).toLocaleString()}</div>;
          },
        },
        {
          field: 'actions',
          type: 'actions',
          headerName: 'Actions',
          width: 100,
          cellClassName: 'actions',
          getActions: (params: GridRowParams<any>) => {
            const { row } = params;
            return [
              <Tooltip title='View Workspace' key='view' arrow placement='top'>
                <GridActionsCellItem
                  icon={<VisibilityOutlined />}
                  label='View'
                  onClick={() => openAccountPage(`/${row.service_id}/buckets`, row.user_id, row.org_id)}
                  color='inherit'
                />
              </Tooltip>,
            ];
          },
        },
      ].filter(Boolean),
    },
  ];

  if (fullScreenCard) {
    const card = cards.find((card) => card.queryKey === fullScreenCard);

    if (card) {
      return (
        <DashboardContainer>
          <ViewError error={logError} setError={setLogError} />
          <FullScreenTable>
            <DashboardTableCard
              card={card}
              onClose={() => setFullScreenCard(null)}
              setFullScreenCard={setFullScreenCard}
              startDate={startDate}
              endDate={endDate}
            />
          </FullScreenTable>
        </DashboardContainer>
      );
    }
  }

  return (
    <DashboardContainer>
      <ViewError error={logError} setError={setLogError} />
      <GridContainer>
        <Grid columns={2}>
          <DashboardTableCard
            card={cards[0]}
            setFullScreenCard={setFullScreenCard}
            startDate={startDate}
            endDate={endDate}
          />
          <DashboardTableCard
            card={cards[1]}
            setFullScreenCard={setFullScreenCard}
            startDate={startDate}
            endDate={endDate}
          />
        </Grid>
        <Grid columns={3}>
          <DashboardTableCard
            card={cards[2]}
            setFullScreenCard={setFullScreenCard}
            startDate={startDate}
            endDate={endDate}
          />
          <DashboardTableCard
            card={cards[3]}
            setFullScreenCard={setFullScreenCard}
            startDate={startDate}
            endDate={endDate}
          />
          <DashboardTableCard
            card={cards[4]}
            setFullScreenCard={setFullScreenCard}
            startDate={startDate}
            endDate={endDate}
          />
        </Grid>
      </GridContainer>
    </DashboardContainer>
  );
};
